import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { fileToBase64, openBase64Pdf } from '../utils/fileUtils';

const MAX_PDF_BYTES = 500 * 1024;

function MultiSelect({ label, options, value, onChange }) {
  const toggle = (opt) => {
    const arr = value || [];
    onChange(arr.includes(opt) ? arr.filter(v => v !== opt) : [...arr, opt]);
  };
  return (
    <div className="field">
      <label>{label}</label>
      <div className="checkbox-group">
        {options.map(opt => (
          <div key={opt} className={`checkbox-pill ${(value || []).includes(opt) ? 'selected' : ''}`}
            onClick={() => toggle(opt)}>{opt}</div>
        ))}
      </div>
    </div>
  );
}

function DocRow({ label, dataKey, nameKey, profile, newFiles, setNewFiles, setError }) {
  const hasExisting = !!profile[dataKey];
  const newFile = newFiles[dataKey];

  function handleChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > MAX_PDF_BYTES) {
      setError(`"${f.name}" is te groot. Maximum is 500 KB.`);
      return;
    }
    setError('');
    setNewFiles(prev => ({ ...prev, [dataKey]: f }));
  }

  return (
    <div className="field">
      <label>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {hasExisting && !newFile && (
          <button type="button" className="btn btn-secondary btn-sm"
            onClick={() => openBase64Pdf(profile[dataKey], profile[nameKey] || `${label}.pdf`)}>
            📄 Huidig bestand bekijken
          </button>
        )}
        <label className={`upload-zone ${newFile ? 'uploaded' : ''}`}
          style={{ flex: 1, minWidth: 200, cursor: 'pointer', padding: '10px 14px' }}>
          <input type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleChange} />
          {newFile
            ? <span style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 500 }}>✅ {newFile.name}</span>
            : <span style={{ fontSize: 13, color: 'var(--text-3)' }}>📎 {hasExisting ? 'Vervangen...' : 'Uploaden...'} (max 500 KB)</span>}
        </label>
      </div>
    </div>
  );
}

export default function ProfielBewerken() {
  const { currentUser, userProfile, setUserProfile } = useAuth();
  const [form, setForm] = useState({ ...userProfile });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [newFiles, setNewFiles] = useState({});
  const [tab, setTab] = useState('persoonlijk');

  const setField = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  async function handleSave() {
    setSaving(true); setError('');
    try {
      const updates = { ...form };

      for (const [key, file] of Object.entries(newFiles)) {
        if (file) {
          updates[key] = await fileToBase64(file);
          updates[key.replace('Data', 'Naam')] = file.name;
        }
      }

      updates.updatedAt = new Date().toISOString();
      await updateDoc(doc(db, 'users', currentUser.uid), updates);
      setUserProfile(prev => ({ ...prev, ...updates }));
      setSaved(true);
      setNewFiles({});
    } catch (e) {
      setError('Fout bij opslaan: ' + e.message);
    }
    setSaving(false);
  }

  const tabs = [
    { id: 'persoonlijk', label: '👤 Persoonlijk' },
    { id: 'adres', label: '📍 Adres' },
    { id: 'kamp', label: '🏕 Kamp info' },
    { id: 'achtergrond', label: '📋 Achtergrond' },
    { id: 'documenten', label: '📄 Documenten' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">👤 Mijn profiel</h1>
        <p className="page-subtitle">Pas je gegevens aan indien nodig</p>
      </div>

      <div className="tabs">
        {tabs.map(t => (
          <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}><span>⚠</span> {error}</div>}

      <div className="card card-padded">
        {tab === 'persoonlijk' && (
          <div>
            <div className="grid-2">
              <div className="field"><label>Voornaam</label>
                <input className="input" value={form.voornaam || ''} onChange={e => setField('voornaam', e.target.value)} /></div>
              <div className="field"><label>Achternaam</label>
                <input className="input" value={form.achternaam || ''} onChange={e => setField('achternaam', e.target.value)} /></div>
            </div>
            <div className="field"><label>Geboortedatum</label>
              <input className="input" type="date" value={form.geboortedatum || ''} onChange={e => setField('geboortedatum', e.target.value)} /></div>
            <div className="field"><label>Rijksregisternummer</label>
              <input className="input" value={form.rijksregister || ''} onChange={e => setField('rijksregister', e.target.value)} placeholder="00.00.00-000.00" /></div>
            <div className="field"><label>Rekeningnummer</label>
              <input className="input" value={form.rekening || ''} onChange={e => setField('rekening', e.target.value)} /></div>
          </div>
        )}

        {tab === 'adres' && (
          <div>
            <div className="grid-2" style={{ gridTemplateColumns: '2fr 1fr' }}>
              <div className="field"><label>Straat</label>
                <input className="input" value={form.straat || ''} onChange={e => setField('straat', e.target.value)} /></div>
              <div className="field"><label>Huisnummer</label>
                <input className="input" value={form.huisnummer || ''} onChange={e => setField('huisnummer', e.target.value)} /></div>
            </div>
            <div className="grid-2">
              <div className="field"><label>Postcode</label>
                <input className="input" value={form.postcode || ''} onChange={e => setField('postcode', e.target.value)} /></div>
              <div className="field"><label>Stad / Gemeente</label>
                <input className="input" value={form.gemeente || ''} onChange={e => setField('gemeente', e.target.value)} /></div>
            </div>
            <div className="field"><label>Telefoonnummer</label>
              <input className="input" value={form.telefoon || ''} onChange={e => setField('telefoon', e.target.value)} /></div>
            <div className="divider" />
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>🆘 Noodcontact</h3>
            <div className="grid-2">
              <div className="field"><label>Naam noodcontact</label>
                <input className="input" value={form.noodcontactNaam || ''} onChange={e => setField('noodcontactNaam', e.target.value)} /></div>
              <div className="field"><label>Relatie</label>
                <select className="input" value={form.noodcontactRelatie || ''} onChange={e => setField('noodcontactRelatie', e.target.value)}>
                  <option value="">Kies...</option>
                  {['Moeder', 'Vader', 'Partner', 'Broer/Zus', 'Vriend/Vriendin', 'Andere'].map(r => <option key={r} value={r}>{r}</option>)}
                </select></div>
            </div>
            <div className="field"><label>Telefoon noodcontact</label>
              <input className="input" value={form.noodcontactTelefoon || ''} onChange={e => setField('noodcontactTelefoon', e.target.value)} /></div>
          </div>
        )}

        {tab === 'kamp' && (
          <div>
            <MultiSelect label="Coach type" options={['Stem', 'Cream Sport']} value={form.coachType} onChange={v => setField('coachType', v)} />
            <MultiSelect label="Groep" options={['Groen', 'Geel', 'Rood', 'Blauw', 'Toekomstheld']} value={form.groep} onChange={v => setField('groep', v)} />
            <div className="field"><label>T-shirt maat</label>
              <select className="input" value={form.tshirtMaat || ''} onChange={e => setField('tshirtMaat', e.target.value)}>
                <option value="">Kies maat...</option>
                {['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'].map(m => <option key={m} value={m}>{m}</option>)}
              </select></div>
            <MultiSelect label="Locatie" options={['Edegem', 'Mortsel', 'Berchem']} value={form.locatie} onChange={v => setField('locatie', v)} />
          </div>
        )}

        {tab === 'achtergrond' && (
          <div>
            <div className="field"><label>Studies of werkervaring</label>
              <textarea className="input" value={form.studies || ''} onChange={e => setField('studies', e.target.value)} rows={4} /></div>
            <div className="field"><label>Hobby's en vaardigheden</label>
              <textarea className="input" value={form.hobbys || ''} onChange={e => setField('hobbys', e.target.value)} rows={4} /></div>
          </div>
        )}

        {tab === 'documenten' && (
          <div>
            <div className="alert alert-warning" style={{ marginBottom: 20 }}>
              <span>ℹ</span>
              <span>PDF-bestanden worden opgeslagen in Firestore. <strong>Maximum 500 KB per bestand.</strong></span>
            </div>
            <DocRow label="CV" dataKey="cvData" nameKey="cvNaam"
              profile={userProfile} newFiles={newFiles} setNewFiles={setNewFiles} setError={setError} />
            <DocRow label="Strafblad" dataKey="strafbladData" nameKey="strafbladNaam"
              profile={userProfile} newFiles={newFiles} setNewFiles={setNewFiles} setError={setError} />
            <DocRow label="Vrijwilligerscontract" dataKey="contractData" nameKey="contractNaam"
              profile={userProfile} newFiles={newFiles} setNewFiles={setNewFiles} setError={setError} />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
          {saved && <div className="alert alert-success"><span>✓</span> Opgeslagen!</div>}
          <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
            {saving ? 'Opslaan...' : '💾 Wijzigingen opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
